import React, { forwardRef, useState } from 'react';

import DynamicWidthInput from '@/components/Common/Input/DynamicWidthInput';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import {
  CarPaymentBlueColorIcon,
  CheckCircleSolidIcon,
  ChevronDownIcon,
  HeadphoneColorIcon,
  NameTransferColorIcon,
  PriceReductionIcon,
} from '@/components/Icon';
import { colors } from '@/const/colors';
import { useLoanCalculator } from '@/hooks/useLoanCalculator';
import { formatNumber } from '@/utils/common';
import { Input } from '@mui/material';

interface CapitalCounselServiceProps {
  isPopup?: boolean;
  price: number;
}

const loanTermsOption = ['12', '24', '36', '46', '60', '72'];

const CapitalGuideView = forwardRef<HTMLDivElement, CapitalCounselServiceProps>(({ isPopup = false, price }, ref) => {
  const { loanCalculatorState, handleInputChange, handleInputBlur, setLoanCalculatorState } = useLoanCalculator(price);
  const [isOpenPopup, setIsOpenPopup] = useState(false);

  const handleOnClickLoanTerm = () => {
    setIsOpenPopup(true);
  };

  const handleClosePopup = () => {
    setIsOpenPopup(false);
  };

  const handleClickOption = (option: string) => {
    setLoanCalculatorState({ ...loanCalculatorState, loanTerm: option });
    handleClosePopup();
  };

  return (
    <>
      <div ref={ref} className="border-b-[8px] border-grey-1 pt-10 px-4">
        <p className="text-[24px] sm280:text-[22px] sm360:text-[24px] font-bold text-center text-grey-11">
          중고화물차
          <br className="hidden sm280:block" /> 저금리 대출
        </p>
        <div className="flex flex-col items-center justify-center bg-grey-0 sm680:flex-row sm680:gap-[35px] sm680:items-center">
          <div className="flex flex-col items-center justify-center sm680:flex-row sm680:gap-6">
            <div className="flex flex-col items-center">
              <div className="flex justify-center items-center max-w-[248px] w-[178px] h-[96px] mt-6 sm680:mt-0 mb-4">
                <img
                  src="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/capital_counsel_service1.png"
                  alt="구매 동행 서비스 트럭 이미지"
                  className="w-full"
                />
              </div>
              <ul className="flex flex-col gap-[14px] text-grey-8 text-[14px] w-[228px]">
                <li className="flex gap-[6px]">
                  <CarPaymentBlueColorIcon />
                  차량대금 전액 대출이 가능해요
                </li>
                <li className="flex gap-[6px]">
                  <CheckCircleSolidIcon color={colors.primary[9]} />
                  신용점수에 영향이 가지 않아요
                </li>
                <li className="flex gap-[6px]">
                  <HeadphoneColorIcon />
                  개인 맞춤으로 대출 상담이 가능해요
                </li>
                <li className="flex gap-[6px]">
                  <NameTransferColorIcon />
                  무방문, 간편하게 서류 접수하세요
                </li>
              </ul>
            </div>
          </div>

          <div className="p-6 rounded-[12px] opacity-90 min-h-[204px] text-[18px] sm280:text-[16px] bg-grey-1 leading-[40px] mt-[30px] w-full mx-[10px]">
            <div className="text-primary-10 text-[12px] font-semibold leading-[14px] flex items-center">
              <PriceReductionIcon color={colors.primary[10]} />
              &nbsp;&nbsp; 최저 금리 6.1% 기준
            </div>
            <DynamicWidthInput
              name="principal"
              placeholder=""
              value={loanCalculatorState.principal ? formatNumber(loanCalculatorState.principal, true) : ''}
              endAdornment="원"
              onChange={(e) => handleInputChange('principal', e.target.value)}
              onBlur={() => handleInputBlur('principal')}
              maxLength={11}
            />
            &nbsp;&nbsp; 차량에
            <br />
            할부 기간&nbsp;&nbsp;
            <Input
              name="loanTerm"
              value={loanCalculatorState?.loanTerm || ''}
              endAdornment={
                <div className="flex min-w-[65px]">
                  <span className="mr-1">개월</span>
                  <ChevronDownIcon color={colors.gray[8]} />
                </div>
              }
              sx={{
                width: '90px',
                marginBottom: '6px',
                fontSize: '18px',
              }}
              readOnly
              onChange={(e) => handleInputChange('loanTerm', e.target.value)}
              onClick={handleOnClickLoanTerm}
            />
            동안 <br />
            <DynamicWidthInput
              name="interestRate"
              placeholder=""
              value={String(loanCalculatorState?.interestRate) || ''}
              endAdornment="%"
              onChange={(e) => handleInputChange('interestRate', e.target.value)}
              onBlur={() => handleInputBlur('interestRate')}
              maxLength={5}
            />{' '}
            금리 적용 시
            <div className=" mt-3 border-grey-3 border-t pt-3">
              <div className="text-primary-10 text-[16px] font-semibold leading-[24px]">예상 금액</div>
              <div className="text-grey-10 font-semibold text-[30px] leading-[36px] mt-2">
                월 {formatNumber(loanCalculatorState.monthlyPayment)}원
              </div>
            </div>
          </div>
        </div>
        <div className={`text-center mt-5 mb-[30px] flex flex-col gap-5 ${!!isPopup ? 'mb-[56px]' : ''}`}>
          <div className="text-[12px] font-semibold text-grey-8 leading-[14px]">
            실제 금리는 개인 신용도에 따라 달라질 수 있습니다.
          </div>
          <div className="text-grey-6 text-[10px] ">
            본 상담 과정에서 대출 한도 및 적합한 상품을 확인하기 위해 최소한의 개인정보(주민등록번호 포함)를 요청드릴 수
            있습니다.
            <br className="hidden sm550:block sm680:block" /> 고객님의 개인정보는 대출 상담 목적으로만 사용되며, 관련
            법규에 따라 안전하게 보호됩니다.
          </div>
        </div>
      </div>

      <BottomSheetPopup isOpen={isOpenPopup} onClose={handleClosePopup} title="할부 기간">
        <div className="p-4">
          {loanTermsOption.map((option, index) => {
            return (
              <div key={index} className="h-[52px] py-4 cusor-pointer" onClick={() => handleClickOption(option)}>
                {option}개월
              </div>
            );
          })}
        </div>
      </BottomSheetPopup>
    </>
  );
});

export default CapitalGuideView;
